import {
  doc, setDoc, getDocs, collection, query, where,
  WhereFilterOp, limit, getDoc, deleteDoc,
  orderBy, collectionGroup,
  QueryFieldFilterConstraint,
  QueryOrderByConstraint,
  FirestoreDataConverter,
  getCountFromServer,
} from 'firebase/firestore'
import FirebaseService from './FirebaseService'

// generic class for entity services
export default class EntityService<T> implements EntityServiceInterface<T> {
  [x: string]: any

  protected collectionName: string
  protected converter: FirestoreDataConverter<T>

  constructor({ collectionName, converter }: { collectionName: string, converter?: FirestoreDataConverter<T> }) {
    this.collectionName = collectionName
    if (converter) this.converter = converter
  }

  /**
   * Getter to get the collection name
   * 
   * @returns string collectionName
   */
  get name(): string { 
    return this.collectionName
  }

  /**
   * Get the FirestoreDataConverter
   * 
   * @returns FirestoreDataConverter<T>
   */
  public getConvertor(): FirestoreDataConverter<T> { 
    return this.converter
  }

  /**
   * Get the Firestore collection
   * 
   * @returns Promise<Query>
   */
  public async getCollection() { 
    const db = await FirebaseService.firestore()
    return collection(db, this.collectionName)
  }

  /**
   * Get the Firestore collection group
   * 
   * @returns Promise<Query>
   */
  public async getCollectionGroup() { 
    const db = await FirebaseService.firestore()
    return collectionGroup(db, this.collectionName)
  }

  /**
   * Create a new record
   * 
   * @param data
   * @returns Promise<T>
   */
  public async create(data: T) {
    const db = await FirebaseService.firestore()
    const record = doc(collection(db, this.collectionName)).withConverter(this.converter)
    const docData = Object.assign(data, {
      id: record.id,
      deleted: false,
    })
    
    await setDoc(record, docData, { merge: true })

    return Object.assign(data, { id: record.id }) as T
  }

  /**
   * Find all records in the collection
   * 
   * @returns Promise<T[]>
   */
  public async all() {
    const db = await FirebaseService.firestore()
    const queryRef = query(collectionGroup(db, this.collectionName)).withConverter(this.converter)
    const records = (await getDocs(queryRef)).docs

    return records.map(doc => doc.data()) as T[]
  }

  /**
   * Find a record by id
   * 
   * @param id
   * @returns Promise<T>
   */
  public async find(id: string) {
    const record = await this.find_by('id', '==', id)
    return record[0] as T
  }

  /**
   * Find a record by field
   * 
   * @param field
   * @param operator
   * @param value
   * @returns Promise<T[]>
   */
  public async find_by(field: string, operator: WhereFilterOp, value: unknown) {
    const db = await FirebaseService.firestore()
    const collectionRef = collectionGroup(db, this.collectionName)
    const queryRef = query(collectionRef, where(field, operator, value)).withConverter(this.converter)
    const records = (await getDocs(queryRef)).docs

    // console.log('DEBUG: find_by', collectionRef, queryRef)
          
    return records.map(doc => doc.data()) as T[]
  }

  /**
   * Get records from the collection
   * 
   * @param count 
   * @returns Promise<T[]>
   */
  public async take(count: number = 1) { 
    const db = await FirebaseService.firestore()
    const collectionRef = collection(db, this.collectionName)
    const queryRef = query(collectionRef, limit(count)).withConverter(this.converter)
      
    return (await getDocs(queryRef)).docs.map(doc => doc.data()) as T[]
  }

  /**
   * Get the first record from the collection
   * 
   * @param count 
   * @returns Promise<T>
   */
  public async first(count: number = 1) { 
    const db = await FirebaseService.firestore()
    const collectionRef = collection(db, this.collectionName)
    const queryRef = query(collectionRef, orderBy('createdAt', 'desc'), limit(count)).withConverter(this.converter)
      
    return (await getDocs(queryRef)).docs.map(doc => doc.data())[0] as T
  }

  /**
   * Find records from the collection using filters and order by constraints
   * 
   * @param filters
   * @param orderBys 
   * @param count
   * 
   * @returns Promise<T[]>
   */
  public async where(filters: QueryFieldFilterConstraint[], orderBys: QueryOrderByConstraint[], count?: number) {
    const db = await FirebaseService.firestore()
    const collectionRef = collectionGroup(db, this.collectionName)
    let queryRef = query(collectionRef, ...filters, ...orderBys, limit(count)).withConverter(this.converter)

    if (count) queryRef = query(queryRef, limit(count))

    const records = (await getDocs(queryRef)).docs
      
    return records.map(doc => doc.data()) as T[]
  }

  /**
   * Update a record
   * 
   * @param id 
   * @param data 
   * @returns Promise<T>
   */
  public async update(id: string, data: Partial<T>) { 
    const db = await FirebaseService.firestore()
    const record = doc(db, this.collectionName, id).withConverter(this.converter)
    await setDoc(record, data, { merge: true })
    return Object.assign(data, { id: id }) as T
  }

  /**
   * Soft delete a record
   * 
   * @param id 
   * @returns Promise<boolean>
   */
  public async delete(id: string) { 
    const db = await FirebaseService.firestore()
    const record = doc(db, this.collectionName, id)
    await setDoc(record, { deleted: true }, { merge: true })
    return true
  }

  /**
   * Restore a soft deleted record
   * 
   * @param id 
   * @returns Promise<T>
   */
  public async restore(id: string) { 
    const db = await FirebaseService.firestore()
    const record = await getDoc(doc(db, this.collectionName, id))

    await setDoc(record.ref, { deleted: false }, { merge: true })
    return record.data() as T
  }

  /**
   * Force delete a record
   * 
   * @param id 
   * @returns Promise<boolean>
   */
  public async forceDelete(id: string) { 
    const db = await FirebaseService.firestore()
    const record = doc(db, this.collectionName, id)
    await deleteDoc(record)
    return true
  }

  /**
   * Count all records in the collection
   * 
   * @returns Promise<number>
   */
  public async count() { 
    const db = await FirebaseService.firestore()
    const querySnapshot = getCountFromServer(collection(db, this.collectionName))

    return (await querySnapshot).data().count
  }

  /**
   * Count records in the collection using filters
   * 
   * @param filters 
   * @returns Promise<number>
   */
  public async countWhere(filters: string[]) {
    const db = await FirebaseService.firestore()
    const collectionRef = collection(db, this.collectionName)
    let queryRef = query(collectionRef)

    filters.map((filter) => {
      const fieldFilter = filter.split(' ')
      try {
        queryRef = query(queryRef, where(fieldFilter[0], fieldFilter[1] as WhereFilterOp, fieldFilter[2]))
      } catch (error) {
        throw new Error('Invalid filter format')
      }
    })
      
    return (await getDocs(queryRef)).size
  }
}

/**
 * Decorator that adds subcollection management methods to an EntityService class.
 * Generates CRUD methods for managing subcollections of the parent entity.
 * 
 * @template T - The type of documents in the subcollection
 * @param name - The name of the subcollection (plural form, e.g. 'comments')
 * @param converter - Optional FirestoreDataConverter for the subcollection documents
 * @returns A class decorator that adds subcollection management methods
 */
export function WithSubcollection<T>(name: string, converter?: FirestoreDataConverter<T>) {
  return function (target: any) {
    const originalMethods = target.prototype

    const singularName = name.slice(0, -1)
    const methodName = singularName.charAt(0).toUpperCase() + singularName.slice(1)

    /**
     * Retrieves all documents from a subcollection for a given parent entity
     * 
     * @param parentId - The ID of the parent entity
     * @returns Promise<T[]> Array of documents from the subcollection
     */
    target.prototype[`get${methodName}s`] = async function (parentId: string) {
      const db = await FirebaseService.firestore()
      const collectionRef = collection(db, this.collectionName, parentId, name)
      const queryRef = query(collectionRef)

      const querySnapshot = queryRef.withConverter(converter)
      return (await getDocs(querySnapshot)).docs.map(doc => doc.data()) as T[]
    }

    /**
     * Adds a new document to a subcollection for a given parent entity
     * 
     * @param parentId - The ID of the parent entity
     * @param data - The document data to add
     * @returns Promise<T> The created document with its ID
     */
    target.prototype[`add${methodName}`] = async function (parentId: string, data: T) {
      const db = await FirebaseService.firestore()
      const record = doc(collection(db, this.collectionName, parentId, name)).withConverter(converter)
      const docData = Object.assign(data, {
        id: record.id,
        deleted: false,
      })

      await setDoc(record, docData, { merge: true })
      return Object.assign(data, { id: record.id }) as T
    }

    /**
     * Updates an existing document in a subcollection
     * 
     * @param parentId - The ID of the parent entity
     * @param id - The ID of the document to update
     * @param data - The updated document data
     * @returns Promise<T> The updated document
     */
    target.prototype[`update${methodName}`] = async function (parentId: string, id: string, data: T) {
      const db = await FirebaseService.firestore()
      const record = doc(db, this.collectionName, parentId, name, id).withConverter(converter)
      await setDoc(record, data, { merge: true })
      return Object.assign(data, { id: id }) as T
    }

    /**
     * Deletes a document from a subcollection
     * 
     * @param parentId - The ID of the parent entity
     * @param id - The ID of the document to delete
     * @returns Promise<boolean> True if deletion was successful
     */
    target.prototype[`delete${methodName}`] = async function (parentId: string, id: string) {
      const db = await FirebaseService.firestore()
      const record = doc(db, this.collectionName, parentId, name, id)
      await deleteDoc(record)
      return true
    }

    // Copy original methods to the prototype
    Object.assign(target.prototype, originalMethods)
  }
}

/* eslint-disable no-unused-vars */
export interface EntityServiceInterface<T> {
  create(data: T): Promise<T>
  all(): Promise<T[]>
  find(id: string): Promise<T>
  find_by(field: string, operator: WhereFilterOp, value: string): Promise<T[]>
  take(count: number): Promise<T[]>
  first(count: number): Promise<T>
  where(filters: QueryFieldFilterConstraint[], orderBys: QueryOrderByConstraint[]): Promise<T[]>
  update(id: string, data: T): Promise<T>
  delete(id: string): Promise<boolean>
  restore(id: string): Promise<T>
  forceDelete(id: string): Promise<boolean>
  count(): Promise<number>
  countWhere(filters: string[]): Promise<number>
  getCollection(): Promise<any>
  getConvertor(): FirestoreDataConverter<T>
  name: string
}
